import React, { useState, useEffect } from "react";
import { Facets, BasicFacetValue, BasicFacet, AttributeFacet } from "../types";
import { parseQueryParams } from "../utils/queryParams";
import { sanitizeAttributeIndexValue } from "../utils/stringUtils";
import { fetchFacetsData } from "../api/catalogApi";
import { eventBus, EVENTS } from "../utils/eventBus";

interface ShopsFacetCardProps {
  groupId: string;
  isExpanded: boolean;
  showMobileTrigger?: boolean;
  expandedFacets?: Record<string, boolean>;
}

export const ShopsFacetCard: React.FC<ShopsFacetCardProps> = ({
  groupId,
  showMobileTrigger = true,
  isExpanded = false,
  expandedFacets = {},
}) => {
  const [facets, setFacets] = useState<Facets | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, Set<string>>
  >({});

  // Load facets data
  const loadFacets = async (params?: any) => {
    try {
      setLoading(true);
      const queryParams = params || parseQueryParams();
      const queryString = new URLSearchParams();

      // Add cp parameters
      if (queryParams.cp && queryParams.cp.length > 0) {
        queryParams.cp.forEach((value: string, index: number) => {
          queryString.append(`cp[${index}]`, value);
        });
      }

      // Add other parameters
      Object.entries(queryParams).forEach(([key, value]) => {
        if (key !== "cp") {
          if (Array.isArray(value)) {
            value.forEach((v) => queryString.append(key, v));
          } else {
            queryString.append(key, value as string);
          }
        }
      });

      const data = await fetchFacetsData(groupId, queryString.toString());
      const shopsData = Object.entries(data)
        .filter(([, value]) => {
          return (value as BasicFacet).type.toLocaleLowerCase() === "shop";
        })
        .reduce((obj, [key, value]) => {
          obj[key] = value as BasicFacet;
          return obj;
        }, {} as Facets);

      setFacets(shopsData);
    } catch (error) {
      console.error("Error loading facets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load facets on mount
  useEffect(() => {
    loadFacets();
  }, [groupId]);

  // Listen for filter changes from other components
  useEffect(() => {
    const handleFiltersChanged = (params: any) => {
      loadFacets(params);
    };

    eventBus.on(EVENTS.FILTERS_CHANGED, handleFiltersChanged);

    return () => {
      eventBus.off(EVENTS.FILTERS_CHANGED, handleFiltersChanged);
    };
  }, []);

  // Listen for filter changes from URL updates
  useEffect(() => {
    const handleFiltersChangedInShops = (params: any) => {
      const newFilters: Record<string, Set<string>> = {};

      params.cp.forEach((filter: string) => {
        const [facet, value] = filter.split(":");
        if (facet && value) {
          if (!newFilters[facet]) {
            newFilters[facet] = new Set<string>();
          }
          newFilters[facet].add(value);
        }
      });

      setSelectedFilters(newFilters);
      loadFacets(params);
    };

    eventBus.on(EVENTS.FILTERS_CHANGED, handleFiltersChangedInShops);

    return () => {
      eventBus.off(EVENTS.FILTERS_CHANGED, handleFiltersChangedInShops);
    };
  }, []);

  // Initialize selected filters from URL on component mount
  useEffect(() => {
    const params = parseQueryParams();
    const initialFilters: Record<string, Set<string>> = {};

    // Process cp parameters to extract facet and value
    params.cp.forEach((filter) => {
      const [facet, value] = filter.split(":");
      if (facet && value) {
        if (!initialFilters[facet]) {
          initialFilters[facet] = new Set<string>();
        }
        initialFilters[facet].add(value);
        if (facet === "attributes" || facet === "attributes_extra") {
          const parts = value.split("|");
          const attribute_name_key = parts[0] + "_" + parts[1];
          expandedFacets[attribute_name_key] = true;
        } else {
          expandedFacets[facet] = true;
        }
      }
    });

    setSelectedFilters(initialFilters);
  }, []);

  // Remove quotes from filter value
  const sanitizeFilterValue = (value: string): string => {
    if (typeof value !== "string") return String(value);
    if (value.startsWith('"') && value.endsWith('"')) {
      return value.substr(1, value.length - 1);
    }
    return value;
  };

  // Handle filter selection
  const handleFilterChange = (
    facetKey: string,
    value: BasicFacetValue,
    checked: boolean,
    facetType: string | null = null
  ) => {
    let sanitizedValue = "";
    if (facetType?.toLocaleLowerCase() === "attribute") {
      sanitizedValue = sanitizeAttributeIndexValue(value.indexValue || "");
    } else {
      sanitizedValue = value.filter;
    }

    // Emit event with facet and value only
    eventBus.emit(EVENTS.FILTER_TOGGLED, {
      facet: facetKey,
      value: sanitizedValue,
      checked: checked,
    });
  };

  // Check if a filter is selected
  const isFilterSelected = (
    facet: BasicFacet | AttributeFacet,
    value: BasicFacetValue
  ): boolean => {
    const sanitizedValue = sanitizeFilterValue(value.filter);
    return selectedFilters[facet.name]?.has(sanitizedValue) || false;
  };

  const handleClick = () => {
    const mobileFilters =
      document.querySelector<HTMLElement>(".category-filters");
    const activeFilters =
      document.querySelector<HTMLElement>(".active-filters");
    const mobileFilterButton = document.querySelector<HTMLButtonElement>(
      ".mobile-filter__trigger"
    );
    if (!mobileFilterButton || !mobileFilters) {
      return;
    }
    if (mobileFilterButton.classList.contains("mobile-filter__trigger--open")) {
      mobileFilters.classList.add("filters-collapsed");
      activeFilters?.classList.add("filters-collapsed");
      mobileFilterButton.classList.remove("mobile-filter__trigger--open");
    } else {
      mobileFilters.classList.remove("filters-collapsed");
      activeFilters?.classList.remove("filters-collapsed");
      mobileFilterButton.classList.add("mobile-filter__trigger--open");
    }
  };

  useEffect(() => {
    const setupMobileFilters = () => {
      const mobileFilters =
        document.querySelector<HTMLElement>(".category-filters");
      const activeFilters =
        document.querySelector<HTMLElement>(".active-filters");
      if (window.matchMedia("(max-width: 1024px)").matches) {
        mobileFilters?.classList.add("filters-collapsed");
        activeFilters?.classList.add("filters-collapsed");
      }
      return () => {
        // No cleanup needed since we're just adding classes
        // The classes will be removed when component unmounts
      };
    };
    const interval = setInterval(() => {
      if (!window.matchMedia("(max-width: 1024px)").matches) {
        clearInterval(interval);
        return;
      }
      if (document.querySelector<HTMLElement>(".category-filters")) {
        setupMobileFilters();
        clearInterval(interval);
        return;
      }
    }, 400);

    const cleanup = setupMobileFilters();

    return () => {
      cleanup?.();
    };
  }, []);

  // Don't render content when collapsed
  if (!isExpanded) {
    return null;
  }

  if (loading) {
    return (
      <div className="ml-2 space-y-1">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // If no facets are available
  if (!facets || Object.keys(facets).length === 0) {
    return null;
  }

  return (
    <div>
      {showMobileTrigger && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClick();
          }}
          className="mobile-filter__trigger"
        >
          Filters
        </button>
      )}

      <div className="ml-2 space-y-2">
        {Object.entries(facets)
          .sort(([, a], [, b]) => (a.weight || 0) - (b.weight || 0))
          .map(([facetKey, facet]) => {
            return facet?.values?.map((item: BasicFacetValue) => {
              if (
                item.filter === "0" ||
                item.count === 0 ||
                facet.label.toLowerCase() === "webshop"
              ) {
                return null;
              }
              const sanitizedFilter = facet.label;
              const isSelected = isFilterSelected(facet, item);

              return (
                <div
                  key={`${facetKey}-${item.filter}`}
                  className="flex items-center"
                >
                  <input
                    type="radio"
                    id={`shop-${facetKey}`}
                    name="shop-filter"
                    checked={isSelected}
                    onChange={(e) => {
                      // First, remove all existing shop selections
                      Object.entries(facets).forEach(([fKey, f]) => {
                        f.values?.forEach((i: BasicFacetValue) => {
                          if (isFilterSelected(f, i)) {
                            handleFilterChange(fKey, i, false, null);
                          }
                        });
                      });

                      // Add the newly selected shop
                      if (e.target.checked) {
                        handleFilterChange(facetKey, item, true, null);
                      }
                    }}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor={`shop-${facetKey}`}
                    className="text-sm text-gray-700 cursor-pointer flex text-left w-full"
                  >
                    <span>{sanitizedFilter}</span>
                  </label>
                </div>
              );
            });
          })}
      </div>
    </div>
  );
};
